import { CellRenderer } from '@dealstack/frontend/shared/components/Investors/Substeps/SecurityTable/CellRenderer';
import { ColumnRenderer } from '@dealstack/frontend/shared/components/Investors/Substeps/SecurityTable/ColumnRenderer';
import { ColumnDefinition } from '@dealstack/frontend/shared/components/Table/Table/types';
import { useTable } from '@dealstack/frontend/shared/components/Table/Table/useTable';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { getLoanDefinitionTypeNameFromType } from '@dealstack/shared/lib/common/business/loans';
import { getSecurityTypeNameFromType } from '@dealstack/shared/lib/common/securities/securities';
import { CurrencyType, PostInvestorDraftInstanceRes } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useCallback } from 'react';

const formatCurrencyValue = (value?: number, currency?: CurrencyType) => {
	const currencyStr = currency ? ` (${currency})` : '';
	return `${value}${currencyStr}`;
};

export const useSecurityTable = (investors?: PostInvestorDraftInstanceRes[]) => {
	const commonUiTranslation = useTranslationCommonUi();

	const columns: ColumnDefinition[] = [
		{
			id: 'name',
			label: commonUiTranslation('investors.name'),
			sortable: true,
			pinned: true,
			width: 150,
		},
		{
			id: 'contact',
			label: commonUiTranslation('investors.contact'),
			pinned: true,
			width: 300,
			subcolumns: [
				{
					id: 'email',
					label: commonUiTranslation('investors.email'),
					sortable: true,
					dataTestId: TEST_ELEMENT_IDS.MIP_CREATE_INVESTORS_UPLOADED_ENTRY,
				},
			],
		},
	];

	const firstInvestor = investors?.[0];

	if (firstInvestor && firstInvestor.securities.length) {
		columns.push(
			...firstInvestor.securities.map((security, index) => ({
				id: `securities-${index}`,
				pinned: false,
				subcolumns: [
					{
						id: `securities-values-${security.type}-${index}`,
						label: security.name,
						subcolumns: [
							{
								id: `securities-${index}-number`,
								label: 'Number',
								sortable: true,
							},
							{
								id: `securities-${index}-cost`,
								label: 'Cost',
								sortable: true,
							},
						],
					},
				],
				label: getSecurityTypeNameFromType(security.type) ?? '',
			}))
		);
	}

	if (firstInvestor && firstInvestor.loans.length) {
		columns.push(
			...firstInvestor.loans.map((loan, index) => ({
				id: `loans-${index}`,
				pinned: false,
				width: 200,
				subcolumns: [
					{
						id: `loans-values-${loan.type}-${index}`,
						label: loan.name,
						width: 200,
						subcolumns: [
							{
								id: `loans-${index}-amount`,
								label: 'Amount',
								width: 200,
								sortable: true,
							},
						],
					},
				],
				label: getLoanDefinitionTypeNameFromType(loan.type) ?? '',
			}))
		);
	}

	const data =
		investors?.map((investor) => ({
			...investor,
			id: investor.email,
			contact: {
				email: investor.email,
			},
			name: `${investor.firstName} ${investor.lastName}`,
		})) ?? [];

	const tableInstance = useTable({
		columns,
		data,
		totalCount: data.length,
		tableHeight: 500,
		rowSize: 56,
		rowSelect: false,
		customCellValueFn: useCallback((column, item) => {
			if (column.id === 'name') {
				return item.name;
			} else if (column.id === 'email') {
				return item.contact.email;
			} else if (column.id.startsWith('loans-') && column.id.endsWith('-amount')) {
				const split = column.id.split('-');
				const loan = item.loans[split[1]];
				return formatCurrencyValue(loan?.amount, loan?.currency);
			} else if (column.id.startsWith('securities-')) {
				const split = column.id.split('-');

				if (split[2] === 'number') {
					return item.securities[split[1]]?.totalNumber;
				} else if (split[2] === 'cost') {
					const security = item.securities[split[1]];
					return formatCurrencyValue(security?.totalCost, security?.currency);
				} else {
					return '??';
				}
			} else {
				return '??';
			}
		}, []),
		CustomCellRenderer: CellRenderer,
		CustomColumnRenderer: ColumnRenderer,
	});

	return { tableInstance };
};
